import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Score Report|Form|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamScoreReports,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsExamScoreReportForm';
export type SPCatalogsExamScoreReportFormLocNames =
	| `${LocNameStart}NotificationUpdateSuccess`
	| `${LocNameStart}NotificationUpdateFailure`
	| `${LocNameStart}LabelLanguage`
	| `${LocNameStart}LabelDeleteTemplates`
	| `${LocNameStart}LabelPassingTemplate`
	| `${LocNameStart}ButtonDownloadPassing`
	| `${LocNameStart}ButtonReplacePassing`
	| `${LocNameStart}LabelFailingTemplate`
	| `${LocNameStart}ButtonDownloadFailing`
	| `${LocNameStart}ButtonReplaceFailing`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonUpdate`
	| `${LocNameStart}ErrorLanguageRequired`
	| `${LocNameStart}ErrorFileSize`
	| `${LocNameStart}ErrorFileType`
	| `${LocNameStart}ErrorFileRequired`
	| `${LocNameStart}TitlePassingReport`
	| `${LocNameStart}TitleFailingReport`
	| `${LocNameStart}ContentFileHint`;

export const spCatalogsExamScoreReportFormLocs: LocDefs<SPCatalogsExamScoreReportFormLocNames> = {
	SPCatalogsExamScoreReportFormNotificationUpdateSuccess: {
		defaultValue: 'Changes saved',
		description: 'Notification message when changes are saved successfully',
		displayPath: `${pathStart}Changes saved`,
		displayType: 'Notification',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Notification.ChangesSaved',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormNotificationUpdateFailure: {
		defaultValue: 'There was an error uploading the file',
		description: 'Notification message when changes are not saved successfully',
		displayPath: `${pathStart}Changes failure`,
		displayType: 'Notification',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Notification.UploadFailed',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormLabelLanguage: {
		defaultValue: 'Language',
		description: 'Label for: Language.',
		displayPath: `${pathStart}Language`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldLabel.Language',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormLabelDeleteTemplates: {
		defaultValue: 'Delete templates for this language',
		description: 'Label for: Delete templates for this language.',
		displayPath: `${pathStart}Delete templates for this language`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldLabel.DeleteTemplates',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormLabelPassingTemplate: {
		defaultValue: 'Passing Template File',
		description: 'Label for: Passing Template File.',
		displayPath: `${pathStart}Passing Template File`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldLabel.PassingTemplateFile',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormButtonDownloadPassing: {
		defaultValue: 'Download',
		description: 'Button for: Download Passing Template File.',
		displayPath: `${pathStart}Download Passing Template File`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.DownloadPassing',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormButtonReplacePassing: {
		defaultValue: 'Replace',
		description: 'Button for: Replace Passing Template File.',
		displayPath: `${pathStart}Replace Passing Template File`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Replace',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormLabelFailingTemplate: {
		defaultValue: 'Failing Template File',
		description: 'Label for: Failing Template File.',
		displayPath: `${pathStart}Failing Template File`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldLabel.FailingTemplateFile',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormButtonDownloadFailing: {
		defaultValue: 'Download',
		description: 'Button for: Download Failing Template File.',
		displayPath: `${pathStart}Download Failing Template File`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.DownloadFailing',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormButtonReplaceFailing: {
		defaultValue: 'Replace',
		description: 'Button for: Replace Failing Template File.',
		displayPath: `${pathStart}Replace Failing Template File`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Replace',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Button for: Cancel.',
		displayPath: `${pathStart}Cancel`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Cancel',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormButtonUpdate: {
		defaultValue: 'Update',
		description: 'Button for: Update.',
		displayPath: `${pathStart}Update`,
		displayType: 'Button',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Button.Update',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormErrorLanguageRequired: {
		defaultValue: 'Language is required',
		description: 'Error for: Language is required.',
		displayPath: `${pathStart}Language is required`,
		displayType: 'Error',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldError.LanguageRequired',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormErrorFileSize: {
		defaultValue: 'The file must not exceed 5 MB',
		description: 'Error for: The file must not exceed 5 MB.',
		displayPath: `${pathStart}File size error`,
		displayType: 'Error',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldError.FileSize',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormErrorFileType: {
		defaultValue: 'The file must be a DOCX format',
		description: 'Error for: The file must be a DOCX format.',
		displayPath: `${pathStart}File type error`,
		displayType: 'Error',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldError.FileType',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormErrorFileRequired: {
		defaultValue: 'Both, a passing templates, and a failing template are required',
		description: 'Error for: Both, a passing templates, and a failing template are required.',
		displayPath: `${pathStart}File required error`,
		displayType: 'Error',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldError.Required',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormTitlePassingReport: {
		defaultValue: 'Passing Score Report',
		description: 'Passing Score Report title.',
		displayPath: `${pathStart}Passing Score Report`,
		displayType: 'Title',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Title.PassingReport',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormTitleFailingReport: {
		defaultValue: 'Failing Score Report',
		description: 'Failing Score Report title.',
		displayPath: `${pathStart}Failing Score Report`,
		displayType: 'Title',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.Title.FailingReport',
		...sharedProperties,
	},
	SPCatalogsExamScoreReportFormContentFileHint: {
		defaultValue: 'The file must be a DOCX format Word document and not exceed 5 MB in size',
		description: 'File field hint.',
		displayPath: `${pathStart}File field hint`,
		displayType: 'Content',
		legacyName: 'Exam.ScoreReports/Catalogs.ScoreReports.FieldHint.FileHint',
		...sharedProperties,
	},
};

export type SPCatalogsExamScoreReportFormLocType = LocMap<SPCatalogsExamScoreReportFormLocNames>;
